import React, { useState, useEffect } from "react";
import { Link } from "gatsby";
import addDays from "date-fns/addDays";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen } from "@fortawesome/free-solid-svg-icons";
import { library } from "@fortawesome/fontawesome-svg-core";
import { IMobileGlobalSearchSummaryProps } from "./MobileGlobalSearchSummaryProps";
import {
  Location,
  Container,
  MobileInputWrapper,
  MobileFindButton,
} from "./MobileGlobalSearchSummaryHelpers";

import { formatDate, parseSearchDate } from "../../../../services/dates";

import { useAppSelector } from "../../../../hooks";
import { searchSelector } from "../../../../redux/slices/Search/search";
import { useScrollVisibility } from "../../../../context/scrollVisibilityContext";

library.add(faPen);

const MobileGlobalSearchSummary: React.FC<IMobileGlobalSearchSummaryProps> = ({
  onEdit,
  theme,
  showLogo,
}) => {
  const search = useAppSelector(searchSelector);
  const { isLogoVisible } = useScrollVisibility();
  const [searchString, setSearchString] = useState(search.searchString);
  const [checkin, setCheckin] = useState(
    search.checkin !== null ? parseSearchDate(search.checkin) : new Date()
  );
  const [checkout, setCheckout] = useState(
    search.checkout !== null
      ? parseSearchDate(search.checkout)
      : addDays(new Date(), 1)
  );

  const [isLoading, setIsLoading] = useState(!searchString);

  useEffect(() => {
    setSearchString(search.searchString);
    setCheckin(parseSearchDate(search.checkin));
    setCheckout(parseSearchDate(search.checkout));
    setIsLoading(false); // Set loading to false once data is fetched
  }, [search]);

  if (isLoading) {
    return <div></div>;
  }

  return (
    <Container
      onClick={onEdit}
      tabIndex={0}
      onKeyDown={(e: { key: string }) => {
        if (e.key === "Enter") {
          onEdit();
        }
      }}
    >
      {!searchString && (
        <Row className="g-0">
          <Col xs={2} className="mx-2">
            <Link
              to={theme.linkPath}
              className={`${showLogo ? "d-block" : "d-none"}`}
            >
              {showLogo && (
                <img
                  src={theme.logo}
                  className={`logo-movile mobile-global-search-logo`}
                  alt={theme.alt}
                />
              )}
            </Link>
          </Col>
          <Location
            as={Col}
            xs={showLogo ? "9" : "12"}
            className={`align-self-center ${!showLogo && "px-2"}`}
          >
            <Form.Group className="mb-0">
              <Form.Label htmlFor="global-search-autocomplete" srOnly>
                Location
              </Form.Label>
              <MobileInputWrapper className="w-100">
                <span className="searchIcon">
                  <FontAwesomeIcon icon="map-marker-alt" />
                </span>
                <input
                  placeholder="City, airport, attraction or hotel"
                  className="rounded w-100 px-2"
                  aria-label="Search Term"
                />
              </MobileInputWrapper>
            </Form.Group>
          </Location>
        </Row>
      )}
      {searchString && searchString.length > 0 && (
        <Row className="d-lg-none  mobile-sticky-header align-items-center justify-content-between w-100 g-0">
          <Col xs={10}>
            {theme.color && theme.logo && isLogoVisible && (
              <Col xs={12} className="align-text-center">
                <Link
                  to={theme.linkPath}
                  className={`${showLogo ? "d-block" : "d-none"}`}
                >
                  {theme.logo && (
                    <img
                      src={theme.logo}
                      className={`logo-movile mobile-global-search-logo`}
                      alt={theme.alt}
                    />
                  )}
                </Link>
              </Col>
            )}
            <Col
              xs={12}
              sm
              className={showLogo ? "hotel-name-container-movile" : ""}
            >
              <Row className="g-0">
                <Col xs={12} className="d-flex search-string">
                  <span className="hotel-name-movile overflow-hidden text-truncate me-2">
                    {searchString}
                  </span>
                </Col>
                <Col className="textLeft d-flex date-string">
                  {checkin && checkout && (
                    <>
                      {formatDate(checkin, "MMM D, YYYY")} -{" "}
                      {formatDate(checkout, "MMM D, YYYY")}
                    </>
                  )}
                </Col>
              </Row>
            </Col>
          </Col>

          <Col xs={2} sm="auto" className="text-end ps-3 pd-5 edit-icon">
            <MobileFindButton>Edit</MobileFindButton>
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default MobileGlobalSearchSummary;
